import ky from "ky"
import { useAuthStore } from "@/stores/auth-store"
import { tryRefreshToken } from "@/lib/auth/token-refresh"
import {
  telemetryAfterResponse,
  telemetryBeforeError,
} from "@/lib/telemetry/api-instrumentation"

const isDev =
  window.location.hostname === "localhost" ||
  window.location.hostname === "127.0.0.1"

const apiUrls: Record<string, string> = {
  "auth-api": "https://accounts.bkwai.com",
  "sensor-api": "https://sensors.bkwai.com",
  "timeseries-api": "https://timeseries.bkwai.com",
  "insights-api": "https://insights.bkwai.com",
  "alerts-api": "https://alerts.bkwai.com",
  "reports-api": "https://reports.bkwai.com",
}

export function resolveApiUrl(service: string): string {
  if (isDev) return `/${service}`
  return apiUrls[service] ?? `/${service}`
}

function createClient(baseUrl: string) {
  return ky.create({
    prefixUrl: baseUrl.endsWith("/") ? baseUrl : `${baseUrl}/`,
    timeout: 30000,
    hooks: {
      beforeRequest: [
        (request) => {
          const token = useAuthStore.getState().accessToken
          if (token) {
            request.headers.set("Authorization", `Bearer ${token}`)
          }
        },
      ],
      beforeRetry: [
        async ({ request, error }) => {
          if (error && "response" in error) {
            const resp = error.response as Response | undefined
            // Some services (e.g. the accounts API) answer an expired/invalid
            // token with 403 rather than 401, so treat both as "try a refresh".
            // If the refresh fails, tryRefreshToken() expires the session and
            // redirects to /login; if it succeeds we replay with the new token.
            if (resp?.status === 401 || resp?.status === 403) {
              const refreshed = await tryRefreshToken()
              if (refreshed) {
                const newToken = useAuthStore.getState().accessToken
                request.headers.set("Authorization", `Bearer ${newToken}`)
              }
            }
          }
        },
      ],
      afterResponse: [telemetryAfterResponse],
      beforeError: [telemetryBeforeError],
    },
    retry: {
      limit: 1,
      statusCodes: [401, 403],
    },
  })
}

export const accountsApi = createClient(resolveApiUrl("auth-api"))
export const sensorsApi = createClient(resolveApiUrl("sensor-api"))
export const timeseriesApi = createClient(resolveApiUrl("timeseries-api"))
export const insightsApi = createClient(resolveApiUrl("insights-api"))
export const alertsApi = createClient(resolveApiUrl("alerts-api"))
export const reportsApi = createClient(resolveApiUrl("reports-api"))
